import { useState, useCallback, useRef, useEffect } from 'react';

export const usePricing = ({ onUpdate, postType = 'POST' }) => {
  const [messagePrice, setMessagePrice] = useState('');
  const onUpdateRef = useRef(onUpdate);
  const prevPriceRef = useRef(0); // Track previous price to detect 0→paid transition
  
  // Keep the ref current
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const handlePriceChange = useCallback((value, mediaItems = []) => {
    // Always preserve the user's input in the state
    setMessagePrice(value);
    
    // Helper function to safely parse price value - preserve user input better
    const parsePrice = (val) => {
      if (!val || val === '') return 0;
      // Don't convert partial inputs like "5." or "0."
      if (val.endsWith('.') && val.split('.').length === 2 && val.split('.')[1] === '') {
        return parseFloat(val.slice(0, -1)) || 0;
      }
      const parsed = parseFloat(val);
      return isNaN(parsed) ? 0 : parsed;
    };

    // For messages without media, just update the price directly
    if (mediaItems.length === 0) {
      const priceValue = parsePrice(value);
      // Don't call onUpdate if we're just dealing with incomplete input
      if (value !== '' || priceValue === 0) {
        onUpdateRef.current?.({
          mediaItems: [],
          messagePrice: priceValue,
          hasPaidContent: priceValue > 0
        });
      }
      return [];
    }
    
    // Apply the 8 post type rules for media locking (only when we have media)
    const shouldForceAllFree = () => {
      // Rule 1: Post without price - all content open
      if (postType === 'POST' && (!value || parseFloat(value) === 0)) return true;
      
      // Rule 3: Subscribers only without price - all content open to subscribers
      if (postType === 'SUBSCRIBERS_ONLY' && (!value || parseFloat(value) === 0)) return true;
      
      // Rule 5: Followers only without price - all content open to followers & subscribers
      if (postType === 'FOLLOWERS_ONLY' && (!value || parseFloat(value) === 0)) return true;
      
      // Rule 7: Target Goal - media shown normally (free) until goal reached
      if (postType === 'TARGET') return true;
      
      // Rule 8: Poll - show media normally with poll
      if (postType === 'POLL') return true;
      
      return false;
    };

    // Force all media to be free for certain post types/conditions
    if (shouldForceAllFree()) {
      prevPriceRef.current = 0; // Reset so the next paid entry triggers a fresh lock
      const updatedItems = mediaItems.map(item => ({ ...item, isFreePreview: true }));
      onUpdateRef.current?.({
        mediaItems: updatedItems,
        messagePrice: parsePrice(value),
        hasPaidContent: false
      });
      return updatedItems;
    }
    
    // Rules 2, 4, 6: Posts with price (regular, subscribers, followers)
    // Allow user to toggle individual media items between free/locked
    const priceValue = parsePrice(value);
    if (value && priceValue > 0) {
      const prevPrice = prevPriceRef.current;
      prevPriceRef.current = priceValue;

      // Only reset isFreePreview flags on the 0→paid transition (initial price entry).
      // When the price stays positive (user editing an already-set price), preserve each
      // item's current flag so user-toggled free/locked choices are not wiped out.
      const isTransitioningFromFree = prevPrice <= 0;
      const updatedItems = isTransitioningFromFree
        ? mediaItems.map(item => ({ ...item, isFreePreview: false }))
        : mediaItems;
      
      onUpdateRef.current?.({
        mediaItems: updatedItems,
        messagePrice: priceValue,
        hasPaidContent: true
      });
      
      return updatedItems;
    }
    // Price cleared back to zero — reset prevPriceRef so next paid entry triggers a fresh lock
    prevPriceRef.current = 0;
    
    // Fallback: no price, make everything free
    const updatedItems = mediaItems.map(item => ({ ...item, isFreePreview: true }));
    
    onUpdateRef.current?.({
      mediaItems: updatedItems,
      messagePrice: 0,
      hasPaidContent: false
    });
    
    return updatedItems;
  }, [postType]);

  const updateParent = useCallback((items, price) => {
    // Use the passed price parameter, fallback to current messagePrice if not provided
    const finalPrice = price !== undefined ? price : messagePrice;
    const updatedData = {
      mediaItems: items,
      messagePrice: finalPrice ? parseFloat(finalPrice) : 0,
      hasPaidContent: items.some(item => !item.isFreePreview) || (finalPrice && parseFloat(finalPrice) > 0)
    };
    onUpdateRef.current?.(updatedData);
  }, [messagePrice]);

  const reset = useCallback(() => {
    setMessagePrice('');
  }, []);

  return {
    messagePrice,
    setMessagePrice,
    handlePriceChange,
    updateParent,
    reset
  };
};
